// Second step of sign-in when a user has two-factor authentication on (T04, T12 §9.1).
// Both `features/auth/client.ts` (twoFactorClient twoFactorPage) and `login.tsx` already send the
// user here, so the route has to exist; without it TanStack Router rejects the navigation. (D-035)
import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { Button } from "@chartvn/ui/button";
import { authClient } from "../features/auth/client";
import { formString } from "../features/auth/form-value";
import { log } from "../telemetry";

const searchSchema = z.object({
  redirect: z.string().optional(),
});

export const Route = createFileRoute("/two-factor")({
  validateSearch: searchSchema,
  component: TwoFactorPage,
});

function TwoFactorPage() {
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setPending(true);
    const code = formString(new FormData(e.currentTarget), "code").trim();
    const target = redirect && redirect.startsWith("/") ? redirect : "/playground";

    const { error: err } = useBackupCode
      ? await authClient.twoFactor.verifyBackupCode({ code })
      : await authClient.twoFactor.verifyTotp({ code });

    setPending(false);
    if (err) {
      // Status only. The code is a credential and never reaches a log (T12 rule 1).
      log.debug("two-factor rejected", err.status);
      setError("That code is not valid. Try again.");
      return;
    }
    void navigate({ to: target });
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-sm flex-col justify-center gap-6 p-6">
      <h1 className="text-2xl font-semibold">Two-factor authentication</h1>
      <p className="text-muted-foreground text-sm">
        {useBackupCode
          ? "Enter one of your backup codes."
          : "Enter the 6-digit code from your authenticator app."}
      </p>
      <form
        onSubmit={(e) => void onSubmit(e)}
        aria-label="Two-factor authentication"
        className="flex flex-col gap-4"
      >
        <label className="flex flex-col gap-1 text-sm">
          {useBackupCode ? "Backup code" : "Authentication code"}
          <input
            name="code"
            type="text"
            inputMode={useBackupCode ? "text" : "numeric"}
            autoComplete="one-time-code"
            autoFocus
            required
            className="border-input bg-background h-9 rounded-md border px-3"
          />
        </label>
        {error && (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        )}
        <Button type="submit" disabled={pending} data-telemetry="two-factor-submit">
          Verify
        </Button>
      </form>
      <Button
        type="button"
        variant="link"
        onClick={() => {
          setUseBackupCode((v) => !v);
          setError(null);
        }}
        data-telemetry="two-factor-toggle-method"
      >
        {useBackupCode ? "Use my authenticator app instead" : "Use a backup code instead"}
      </Button>
    </main>
  );
}
